'use client';

import { useRouter } from 'next/navigation';
import { ReactNode, useState } from 'react';

import {
    Alert,
    Button,
    ButtonVariant,
    Column,
    Columns,
    Spacer,
    SpacerSize,
    StatusState,
    Text,
    TextLevels,
    TypographyAlign,
    TypographyBold,
} from '@bytel/trilogy-react';

import { getAppConfig } from '@services/config';
import { PlanPrice } from '@services/products';

import { MobilePlanType } from '@app-types/plan';

import { RocCard } from '@components/roc-card';

import { useMarketings } from '@hooks/use-marketings';

import { getCart } from '@helpers/cart';
import { paths } from '@helpers/path';
import { getNeededElementGencodes } from '@helpers/product';
import { formatTagText } from '@helpers/tracking';

import { PlanTypeId } from '@constants/plan';
import { ERROR_CONTEXT } from '@constants/provider';

import { EligibilityResults, JourneyConfigType, sendTrackingFromEligibilityMismatchError } from '@eligibility/helpers';
import { OfferCard } from '@home-mobile/components';
import { sendPlanDetailsTracking } from '@home-mobile/helpers';
import { ModalPlanDetailsContainer } from 'src/app/(offer-wall)/_components/offer-wall-container/modal-plan-details-container';

import { useFetchSinglePlan } from '../../_helpers/use-fetch-single-plan';
import { Forecast } from '../forecast';
import { ImageCtaBox } from '../image-cta-box';
import { ByouPlanCard } from './byou-plan-card/byou-plan-card';

type EligibilityResultErrorProps = {
    error: ERROR_CONTEXT;
    address?: ReactNode;
    results: EligibilityResults;
    planId?: string;
    onAddToCart: (gencode: string) => void;
    journeyConfig: JourneyConfigType;
};

export function EligibilityResultError({ error, ...props }: Readonly<EligibilityResultErrorProps>) {
    if (error === ERROR_CONTEXT.ERROR) {
        return <TechnicalError {...props} />;
    }

    if (error === ERROR_CONTEXT.NO_OFFER) {
        return <NoOfferError {...props} />;
    }

    if (error === ERROR_CONTEXT.MISMATCH_CONFIG) {
        return <MismatchConfigError {...props} />;
    }

    return null;
}

function MismatchConfigError({
    children,
    results,
    address,
    planId,
    onAddToCart,
    journeyConfig,
}: {
    children?: ReactNode;
    results: EligibilityResults;
    address?: ReactNode;
    planId?: string;
    onAddToCart: (gencode: string) => void;
    journeyConfig: JourneyConfigType;
}) {
    const [marketingDiscounts] = useMarketings();
    const [offerSelectedForDetails, setOfferSelectedForDetails] = useState<MobilePlanType>();
    const router = useRouter();
    const appConfig = getAppConfig();

    const { mobilePlanDetailed, isLoading } = useFetchSinglePlan(planId);

    const openOfferDetails = (offer: MobilePlanType) => {
        sendPlanDetailsTracking(offer);
        setOfferSelectedForDetails(offer);
    };

    const onAddMobilePlanToCart = async (chosenOffer?: MobilePlanType) => {
        if (chosenOffer) {
            sendTrackingFromEligibilityMismatchError(
                journeyConfig,
                `Continuer_avec_ce_forfait_${formatTagText(chosenOffer.name)}`,
            );
            const cart = await getCart();
            const autoAdd = getNeededElementGencodes(chosenOffer);
            await cart.addProductsToCart([chosenOffer.gencode, ...autoAdd]);
            router.push(paths.cart);
        }
        return new Promise<void>(() => router.push('/panier'));
    };

    if (isLoading) {
        return null;
    }

    const { has5gOffer, hasXgboxOffer } = getXgboxOffersDetails(results);

    return (
        <>
            {address}
            {mobilePlanDetailed ? (
                <>
                    <Text
                        level={TextLevels.ONE}
                        typo={[TypographyAlign.TEXT_CENTERED, TypographyBold.TEXT_WEIGHT_SEMIBOLD]}
                    >
                        Vous pouvez continuer avec votre forfait sans engagement
                    </Text>
                    <Spacer size={SpacerSize.NINE} />
                    <Columns className='is-centered'>
                        <Column desktopSize={4} widescreenSize={3}>
                            {mobilePlanDetailed.categories.some((category) =>
                                [PlanTypeId.SOWO, PlanTypeId.SOWO_SPECIALS].includes(category as PlanTypeId),
                            ) ? (
                                <ByouPlanCard mobilePlan={mobilePlanDetailed} journeyConfig={journeyConfig} />
                            ) : (
                                <OfferCard
                                    offer={mobilePlanDetailed}
                                    showBlockedPlanZone
                                    marketingDiscounts={marketingDiscounts}
                                    openOfferDetails={openOfferDetails}
                                    isConnected={false}
                                    isPhoneLoading={false}
                                    isSmallPrice={false}
                                    hatInLine={false}
                                    onAddToCart={() => onAddMobilePlanToCart(mobilePlanDetailed)}
                                    customCtaLabel='Continuer avec ce forfait'
                                    customName={`Forfait ${mobilePlanDetailed.dataEnvelope}`}
                                />
                            )}
                        </Column>
                    </Columns>
                    {offerSelectedForDetails && (
                        <ModalPlanDetailsContainer
                            offerSelectedForDetails={offerSelectedForDetails}
                            marketingDiscounts={marketingDiscounts}
                            onClose={() => setOfferSelectedForDetails(undefined)}
                            onAddToCart={() => onAddMobilePlanToCart(mobilePlanDetailed)}
                            phoneInCart={undefined}
                            customPrice={mobilePlanDetailed?.details.price as unknown as PlanPrice}
                        />
                    )}
                </>
            ) : (
                <XgboxContent results={results} onAddToCart={onAddToCart} journeyConfig={journeyConfig} />
            )}
            <Spacer size={SpacerSize.NINE} />
            {mobilePlanDetailed && hasXgboxOffer ? (
                <>
                    <Columns className=''>
                        <Column className='is-fullheight'>
                            <ImageCtaBox
                                backgroundImage={`${appConfig.assets.cmsImages.url}/eligibility/${has5gOffer ? '5g' : '4g'}-box.webp`}
                                title={`La ${has5gOffer ? '5G' : '4G'} box haut débit est disponible chez vous 🚀`}
                                text={
                                    <Text level={TextLevels.ONE}>
                                        En plus, elle est <strong>sans engagement</strong> et ça, c’est parfait en
                                        attendant la fibre !
                                    </Text>
                                }
                                cta={
                                    <Button
                                        className=''
                                        variant={ButtonVariant.PRIMARY}
                                        fullwidth
                                        onClick={() =>
                                            sendTrackingFromEligibilityMismatchError(
                                                journeyConfig,
                                                `Découvrir_la_${has5gOffer ? '5G' : '4G'}_box`,
                                            )
                                        }
                                        href={`/offres-internet/${has5gOffer ? '5g-box' : '4g-box'}`}
                                    >
                                        Découvrir la {has5gOffer ? '5G' : '4G'} box
                                    </Button>
                                }
                            />
                        </Column>
                        <Column>
                            <Forecast results={results} journeyConfig={journeyConfig} />
                        </Column>
                    </Columns>
                </>
            ) : (
                <Columns className='is-centered'>
                    <Column size={6}>
                        <Forecast results={results} journeyConfig={journeyConfig} />
                    </Column>
                </Columns>
            )}

            {children}
        </>
    );
}

function XgboxContent({
    results,
    onAddToCart,
    journeyConfig,
}: {
    results: EligibilityResults;
    onAddToCart: (gencode: string) => void;
    journeyConfig: JourneyConfigType;
}) {
    const { has5gOffer, hasXgboxOffer } = getXgboxOffersDetails(results);

    if (!hasXgboxOffer) {
        return (
            <Text level={TextLevels.ONE} typo={[TypographyAlign.TEXT_CENTERED, TypographyBold.TEXT_WEIGHT_SEMIBOLD]}>
                Vous n&apos;êtes éligible à aucune offre
            </Text>
        );
    }

    return (
        <>
            <Text level={TextLevels.ONE} typo={[TypographyAlign.TEXT_CENTERED, TypographyBold.TEXT_WEIGHT_SEMIBOLD]}>
                Vous pouvez profiter de la {has5gOffer ? '5G' : '4G'} box {has5gOffer ? 'très' : ''} haut débit
            </Text>
            <Spacer size={SpacerSize.NINE} />
            <Columns className='is-centered'>
                {Object.values(results.offers)?.[0]?.map((offer) => (
                    <Column
                        key={`${offer.rangeNg}_${offer.commercialSeries}`}
                        mobileSize={12}
                        tabletSize={6}
                        desktopSize={4}
                        data-cy={`${offer.rangeNg}_${offer.commercialSeries}`}
                    >
                        <RocCard
                            plan={offer.mapToFaiType()}
                            cta={{
                                onClick: () => {
                                    sendTrackingFromEligibilityMismatchError(
                                        journeyConfig,
                                        `Ajouter_au_panier_${formatTagText(offer.name)}`,
                                    );
                                    onAddToCart(offer.sku);
                                },
                                text: 'Ajouter au panier',
                                color: ButtonVariant.CONVERSION,
                            }}
                        />
                    </Column>
                ))}
            </Columns>
        </>
    );
}

function NoOfferError({
    address,
    results,
    journeyConfig,
}: {
    address?: ReactNode;
    results: EligibilityResults;
    journeyConfig: JourneyConfigType;
}) {
    return (
        <>
            {address}
            <Columns className='is-centered'>
                <Column size={6}>
                    <Alert
                        title='Ce logement ne semble malheureusement éligible à aucune offre'
                        description='Vous pouvez modifier l’adresse pour tester un autre logement, ou bien renseigner vos coordonnées pour être prévenu de l’arrivée de la fibre. '
                        status={StatusState.ERROR}
                        display
                    />
                    <Spacer size={SpacerSize.NINE} />
                    <Forecast results={results} journeyConfig={journeyConfig} />
                </Column>
            </Columns>
        </>
    );
}
function TechnicalError({ address }: Readonly<{ address?: ReactNode }>) {
    return (
        <>
            {address}

            <Alert
                title='Aïe, nous sommes désolés. Une erreur est survenue de notre côté.'
                description='Vous pouvez modifier l’adresse pour relancer le test.'
                status={StatusState.ERROR}
                display
            />
        </>
    );
}

function getXgboxOffersDetails(results: EligibilityResults) {
    const has5gOffer = results.results.box5g.offers.length > 0;
    const has4gOffer = results.results.box4g.offers.length > 0;
    const hasXgboxOffer = has5gOffer || has4gOffer;

    return {
        has5gOffer,
        has4gOffer,
        hasXgboxOffer,
    };
}
